import { getInstanceDefinition } from '../../cards/cardLookup';
import { cardDefinitions } from '../../cards/cardDefinitions.generated';
import { getPlayerCollectionInstanceIds, getPlayerById } from '../../state/selectors';
import { isHeroLike } from '../../cards/effectHelpers';

// Matt: "This is really a game about combos... what you want is just a
// handful of cards that work well together, and then you wanna draw them
// consistently." villageHeuristics.js's cardValue/costEfficiency score
// every card in total isolation - this file adds the missing cross-card
// layer, grounded in the actual printed ability text of all 596 cards
// across all 7 sets (verified directly against
// cardDefinitions.generated.js, not guessed - every card id below was
// looked up and its exact ability text re-read before being added here).
//
// Two real patterns recur in the ability text:
//  - "Lords"/count-scaling: a card's bonus scales with how many cards of a
//    trait the player owns (e.g. hero-garrison-l3: "+3 Physical Attack
//    while equipped with a weapon").
//  - Resource-chain generator/consumer: cards that produce a board-state
//    resource (Light, Disease, XP, Spells) paired with cards whose value
//    scales on that resource (e.g. the many Cleric "destroy 1 disease to
//    draw a card" heroes across every set).
//
// Each cluster's `counter(state, playerId)` returns "how much of this
// resource does the player currently own" - always computed over
// getPlayerCollectionInstanceIds (hand+deck+discard+in-play+set-aside),
// the same selector villageHeuristics.js's ownedCountOf already uses -
// NEVER live hand/combat/dungeon-hall state. Many cards' real printed
// trigger is combat-time ("present" typically means "in hand during this
// fight," "monster present" means "in the dungeon hall"), which isn't
// knowable at Village-buy time - the owned-collection total is a
// deliberate, documented PROXY for "how much of this resource this deck
// tends to produce," not an attempt to simulate the exact combat trigger.
// Cards whose real condition is dungeon/hall/environment-scoped with no
// ownable-card counterpart (Darkness, monster-hall composition, "if a
// monster is present") are excluded entirely rather than mis-modeled -
// see the omissions noted per cluster below.
//
// Trophy-timed monster "lords" (monster-culova "+1 Physical Attack to
// each fighter", monster-margr/sathosh/chirog/murden - the Cleric/Ranger/
// Wizard/Thief equivalents) are one-shot bonuses applied only at the
// instant the monster is defeated, not persistent effects, AND monsters
// are never a Village buy target - they contribute nothing here by
// design, not oversight.
//
// Explicitly out of scope: inverse-direction scalers (the 5 Djinn
// monsters - monster-ghul/ifrit/iblis/marid/majnun - that punish owning
// ZERO of a class; hero-blind-l3/l4's "bonus if NO Light present";
// village-owl-eyes' "Attack -2 per point of Light") - modeling a scaler
// that wants LESS of a resource needs a `direction` field this schema
// doesn't have. This plan is about deliberately building a combo, not
// defending against anti-combo monsters - a documented future extension.

// First empirical sweep (yarn ai-tournament --randomized-village,
// synergyWeight 0.5/1/2/4 vs. 0, 300 games/matchup) came back badly
// NEGATIVE and worsening with weight - the acquired-card tally showed
// basic-torch/basic-longspear as the top 2 most-bought cards, reproducing
// the exact Regular/Longspear structural-dominance bug DIFFICULTY_PRESETS'
// own costFloor/isHeroLike fix already solved once this session, via a new
// path: a cheap, always-available Basic-trait card that is ALSO its own
// cluster's resource (torch generates the Light light-total counts;
// longspear IS the Weapon/Polearm weapon-equip-count/polearm-count count)
// creates a runaway self-reinforcing loop - buying one raises the score of
// buying the next, checked only by diminishingReturnsRate (which dampens
// repeat buys of the SAME card, not the whole cluster). Fixed two ways:
// Basic-trait cards are excluded from every generic trait/resource
// cluster's membership and counter (see EXCLUDE_BASIC below) - they stay
// eligible for cardValue/costEfficiency's normal scoring, just not as
// synergy-cluster fodder - and the weights were cut roughly 3x/3x and the
// cap roughly halved so synergy is a nudge relative to typical cardValue
// magnitudes (3-30), not an overriding force.
//
// DEFAULT_CLUSTER_CAP is villageHeuristics.js's DEFAULT_BUY_WEIGHTS.
// clusterCap default - Matt: "number of cards in the synergy" as a tunable
// knob - directly answers "how big should a combo package be before the AI
// stops caring about growing it further." Passed into synergyScore as a
// param (not read from a module constant) so scripts/aiRoundRobin.mjs can
// test different values per strategy.
const DEFAULT_CLUSTER_CAP = 5;
const MEMBER_WEIGHT = 0.3;
const SCALER_WEIGHT = 0.5; // the "payoff" card of a cluster weighs slightly more than a plain member

function isBasic(def) {
  return def.traits.includes('Basic');
}

function ownedTraitCount(state, playerId, trait) {
  return getPlayerCollectionInstanceIds(state, playerId)
    .filter((id) => {
      const def = getInstanceDefinition(state, id);
      return !isBasic(def) && def.traits.includes(trait);
    }).length;
}

const CLUSTERS = [
  // --- Trait-count "lords" ---
  {
    id: 'hero-count',
    // hero-clan-l1..l4 (WC, Dwarf/Fighter): "Add Physical Attack +1/+2/+3/+4
    // to each other equipped hero." hero-glamercast-l1..l3 (ToR, Human/
    // Thief/Wizard): "Give each other hero Magic Attack +1/+1/+2" (l3 also
    // "Gain 1 XP per hero," a hero-count-scaled generator for xp-total
    // below, not itself an xp-total scaler). Both scale with heroes in
    // play, approximated here by heroes owned (isHeroLike matches Regular
    // too, consistent with how the rest of this codebase treats Regular
    // as hero-equivalent - see villageHeuristics.js's own cardValue).
    // Regular is itself Basic-trait, so it's excluded here the same way
    // every other cluster excludes Basics (see EXCLUDE_BASIC comment above)
    // - a deck full of cheap Regulars shouldn't inflate Clan/Glamercast's
    // score the way real named heroes should.
    memberCheck: (def) => isHeroLike(def) && !isBasic(def),
    counter: (state, playerId) => getPlayerCollectionInstanceIds(state, playerId)
      .filter((id) => {
        const def = getInstanceDefinition(state, id);
        return isHeroLike(def) && !isBasic(def);
      }).length,
    scalerDefIds: new Set([
      'hero-clan-l1', 'hero-clan-l2', 'hero-clan-l3', 'hero-clan-l4',
      'hero-glamercast-l1', 'hero-glamercast-l2', 'hero-glamercast-l3',
    ]),
  },
  {
    id: 'weapon-equip-count',
    // hero-garrison-l2/l3 (Start): "+1/+3 Physical Attack while equipped
    // with a weapon." hero-stoneguard-l2/l3/l4 (WC): "+2/+3/+5 while
    // equipped" (a flat, not per-weapon-count, bonus - modeled via the
    // same proportional counter as a documented approximation, capped by
    // CLUSTER_MEMBER_CAP like every other entry).
    memberTrait: 'Weapon',
    counter: (state, playerId) => ownedTraitCount(state, playerId, 'Weapon'),
    scalerDefIds: new Set(['hero-garrison-l2', 'hero-garrison-l3', 'hero-stoneguard-l2', 'hero-stoneguard-l3', 'hero-stoneguard-l4']),
  },
  {
    id: 'mercenary-count',
    // hero-stoneguard-l2/l3/l4 (WC): "Add Physical Attack +1/+1/+2 for
    // each mercenary present" - a separate ability line from the
    // weapon-equip bonus above.
    memberTrait: 'Mercenary',
    counter: (state, playerId) => ownedTraitCount(state, playerId, 'Mercenary'),
    scalerDefIds: new Set(['hero-stoneguard-l2', 'hero-stoneguard-l3', 'hero-stoneguard-l4']),
  },
  {
    id: 'villager-count',
    // village-village-headman (Numenera): "Add Physical Attack +1 for each
    // villager present (including this one)." village-bloodhound (CoB):
    // "+1 Physical Attack if at least 1 OTHER Bloodhound is present" - its
    // precise text is self-referential (copies of itself), approximated
    // here as a general Villager-trait scaler since Bloodhound is itself
    // Villager-trait too.
    memberTrait: 'Villager',
    counter: (state, playerId) => ownedTraitCount(state, playerId, 'Villager'),
    scalerDefIds: new Set(['village-village-headman', 'village-bloodhound']),
  },
  {
    id: 'polearm-count',
    // village-partisan (Numenera): "Add Physical Attack +1 for each other
    // equipped polearm present." basic-regular's "if equipped with a
    // polearm, draw a card" is the same combo from the other side but
    // lives in the regular-economy cluster below instead, to avoid
    // double-counting one card in two clusters for the same relationship.
    memberTrait: 'Polearm',
    counter: (state, playerId) => ownedTraitCount(state, playerId, 'Polearm'),
    scalerDefIds: new Set(['village-partisan']),
  },
  {
    id: 'ranger-count',
    // village-short-bow (ItA): "+2 Physical Attack while equipped to a
    // ranger." village-dart-thrower (Numenera): "Rangers of any Strength
    // may equip this weapon... this weapon's Attack can apply to rank 1."
    // Both are class-conditional equipment, not proportional count-scalers
    // - approximated the same way as weapon-equip-count above.
    memberTrait: 'Ranger',
    counter: (state, playerId) => ownedTraitCount(state, playerId, 'Ranger'),
    scalerDefIds: new Set(['village-short-bow', 'village-dart-thrower']),
  },
  {
    id: 'cleric-count',
    // village-chalice-mace (WC): "Clerics require 3 less Strength to
    // equip. Add Light +1 while equipped to a cleric." village-ray-emitter
    // (Numenera): "Add Light +1 if a cleric is present."
    memberTrait: 'Cleric',
    counter: (state, playerId) => ownedTraitCount(state, playerId, 'Cleric'),
    scalerDefIds: new Set(['village-chalice-mace', 'village-ray-emitter']),
  },
  {
    id: 'spell-count',
    // hero-harruli-l1/l2 (WC): "+1/+2 Magic Attack per spell present."
    // hero-forcemage-l1 (CoB): "+1 Magic Attack for each spell present"
    // (l2/l3 draw-and-convert rather than count-scale, included anyway as
    // members of the same spell-chasing family). hero-blademage-l2/l3
    // (RoC): converts an owned spell into a weapon - becomes better as
    // more spells are owned to convert. hero-spellsword-l1/l2/l3 (CoB):
    // "+1/+1/+2 Magic Attack per magic item/magic weapon/spell present."
    memberTrait: 'Spell',
    counter: (state, playerId) => ownedTraitCount(state, playerId, 'Spell'),
    scalerDefIds: new Set([
      'hero-harruli-l1', 'hero-harruli-l2',
      'hero-forcemage-l1', 'hero-forcemage-l2', 'hero-forcemage-l3',
      'hero-blademage-l2', 'hero-blademage-l3',
      'hero-spellsword-l1', 'hero-spellsword-l2', 'hero-spellsword-l3',
    ]),
  },

  // --- Resource-chain generator/consumer (board-state total, not a trait count) ---
  {
    id: 'light-total',
    // Sums each owned card's own `light` field - an approximation of "how
    // much Light this deck tends to produce," distinct from
    // lightDarkness.js's computeTotalLight (scoped to the live hand at
    // combat time with a hallRankIndex, not callable from the Village
    // phase). hero-veris-l3/l4 (ItA): "Add Physical/Magic Attack equal to
    // the total Light present." hero-chalice-l2 (ItA): "Add Physical
    // Attack +1 for each Light item present." (veris-l1/l2 generate Light
    // onto equipped weapons but don't themselves scale with owned Light,
    // so they're not scalers here - their own value is captured by normal
    // cardValue via their printed stats. basic-torch is excluded from both
    // the member check and the counter (see EXCLUDE_BASIC comment above) -
    // it's the single biggest driver of the runaway-loop regression this
    // cluster caused before that fix.
    memberCheck: (def) => (def.light || 0) > 0 && !isBasic(def),
    counter: (state, playerId) => getPlayerCollectionInstanceIds(state, playerId)
      .reduce((sum, id) => {
        const def = getInstanceDefinition(state, id);
        return sum + (isBasic(def) ? 0 : (def.light || 0));
      }, 0),
    scalerDefIds: new Set(['hero-veris-l3', 'hero-veris-l4', 'hero-chalice-l2']),
  },
  {
    id: 'disease-count',
    // Disease cards are inflicted (not bought), so this cluster is
    // scaler-only - no card ever gets a "member" bonus for owning disease,
    // there's no buy decision that adds one. Sums owned disease-type cards
    // as a proxy for "how disease-prone this deck's dungeon runs tend to
    // be" once these consumer heroes are in the mix. This is genuinely the
    // largest, most cross-set-supported chain found - a "destroy 1 disease
    // to draw a card" (or Magic Attack) Cleric exists in every single set:
    // hero-cathedral-l1 (Start), hero-chalice-l1/l2 (ItA), hero-charming-
    // l1/l2/l3 (Numenera), hero-divine-l1/l3/l4 (WC - l2's line combines
    // disease+monster-present and is excluded, monster-present isn't
    // modelable), hero-drua-l2/l3 (ToR), hero-honormain-l1/l2/l3 (RoC),
    // hero-profaned-l2/l3 (RoC), hero-regian-l1/l2/l3/l4 (ItA), hero-
    // woodfolk-l2/l3/l4 (WC), hero-woodguard-l3 (RoC), village-bolt-of-
    // purity (ItA), village-curative-draught (Start), village-hedge-witch
    // (RoC).
    counter: (state, playerId) => getPlayerCollectionInstanceIds(state, playerId)
      .filter((id) => getInstanceDefinition(state, id).type === 'disease').length,
    scalerDefIds: new Set([
      'hero-cathedral-l1',
      'hero-chalice-l1', 'hero-chalice-l2',
      'hero-charming-l1', 'hero-charming-l2', 'hero-charming-l3',
      'hero-divine-l1', 'hero-divine-l3', 'hero-divine-l4',
      'hero-drua-l2', 'hero-drua-l3',
      'hero-honormain-l1', 'hero-honormain-l2', 'hero-honormain-l3',
      'hero-profaned-l2', 'hero-profaned-l3',
      'hero-regian-l1', 'hero-regian-l2', 'hero-regian-l3', 'hero-regian-l4',
      'hero-woodfolk-l2', 'hero-woodfolk-l3', 'hero-woodfolk-l4',
      'hero-woodguard-l3',
      'village-bolt-of-purity', 'village-curative-draught', 'village-hedge-witch',
    ]),
  },
  {
    id: 'xp-total',
    // Reads player.xp directly (a real persistent resource, not an owned-
    // collection proxy) rather than counting cards - the one cluster whose
    // counter isn't collection-derived. Scaler-only, same reasoning as
    // disease-count (XP isn't bought as a card). village-soulfire (WC):
    // "Discard up to 5 XP. Add Magic Attack +1 for each XP discarded."
    // village-drill-sergeant (WC): "Draw 1 card for each 2 XP you possess
    // (round down), to a maximum of 5 cards."
    counter: (state, playerId) => getPlayerById(state, playerId).xp,
    scalerDefIds: new Set(['village-soulfire', 'village-drill-sergeant']),
  },

  // --- Named-card mini-economy (not trait-scaling) ---
  {
    id: 'regular-economy',
    // A self-contained package around basic-regular specifically (ToR/
    // Start): basic-regular itself ("if equipped with a polearm, draw a
    // card"), hero-whetmage-l1 ("Level up a Regular or another level 1
    // hero"), village-royal-summons ("Add 3 Regulars from the village to
    // your hand"). monster-necrophidius's free Regular level-up is real
    // rules context but excluded (monster, never buyable).
    memberDefIds: new Set(['basic-regular']),
    counter: (state, playerId) => getPlayerCollectionInstanceIds(state, playerId)
      .filter((id) => getInstanceDefinition(state, id).id === 'basic-regular').length,
    scalerDefIds: new Set(['hero-whetmage-l1', 'village-royal-summons']),
  },
];

// Shared by synergyScore (below) and villageSetup.js's synergy-preferred
// village generation - a pure classification of which clusters `def`
// participates in and in which role(s), with no live game state involved
// (unlike counter(), which needs a player's actual collection). Same
// memberTrait/memberCheck/memberDefIds precedence as synergyScore used to
// inline directly - factored out here so both callers can never drift
// apart on what counts as a member vs. a scaler.
function clusterRolesFor(def) {
  const roles = [];
  for (const cluster of CLUSTERS) {
    // Basic-trait cards never register as a trait-cluster MEMBER (see
    // EXCLUDE_BASIC comment above) - memberCheck clusters that need this
    // guard (hero-count, light-total) already bake it into their own
    // memberCheck; memberDefIds clusters (regular-economy) are a
    // deliberate, hand-picked exception to the Basic exclusion, not
    // subject to it.
    const isMember = cluster.memberTrait ? (!isBasic(def) && def.traits.includes(cluster.memberTrait))
      : cluster.memberCheck ? cluster.memberCheck(def)
        : cluster.memberDefIds ? cluster.memberDefIds.has(def.id) : false;
    const isScaler = cluster.scalerDefIds.has(def.id);
    if (isMember) roles.push({ clusterId: cluster.id, role: 'member' });
    if (isScaler) roles.push({ clusterId: cluster.id, role: 'scaler' });
  }
  return roles;
}

// Matt: "isn't that [AI tournament synergy] stuff useful here" (re: a
// house-rule request for village generation to prefer synergistic cards
// over pure randomness) - yes: this is the exact same CLUSTERS table the
// AI's own buy-heuristic uses, re-exposed as a pure per-card lookup so
// villageSetup.js can bias its random draw toward cards that share a
// cluster with ones already selected, without duplicating (and risking
// drifting from) the cluster definitions above.
export function getClusterRoles(defId) {
  const def = cardDefinitions.find((d) => d.id === defId);
  return def ? clusterRolesFor(def) : [];
}

// isMember/isScaler membership, summed across every cluster `def`
// participates in. A cluster MEMBER is worth more once the player already
// owns a SCALER for that cluster (buying the 3rd Fighter-Weapon matters
// more once Garrison is owned); a SCALER is worth more as the counter
// rises (Garrison is better with 4 weapons owned than 1). Both read the
// same live counter, so they reinforce each other automatically as the
// deck accumulates either side - "commit and deepen" with no new mutable
// AI state, purely a function of current `state`.
//
// activeClusterIds (Set<string> | null): restricts scoring to only the
// named cluster ids, for isolating which SPECIFIC combo family actually
// helps (scripts/rosters/synergy-clusters.json builds one strategy per
// cluster id this way) - null/omitted scores every cluster, today's exact
// behavior, zero regression risk for every existing caller.
export function synergyScore(state, playerId, def, clusterCap = DEFAULT_CLUSTER_CAP, activeClusterIds = null) {
  let bonus = 0;
  for (const { clusterId, role } of clusterRolesFor(def)) {
    if (activeClusterIds && !activeClusterIds.has(clusterId)) continue;
    const cluster = CLUSTERS.find((c) => c.id === clusterId);
    const counter = cluster.counter(state, playerId);
    if (role === 'member') bonus += Math.min(counter, clusterCap) * MEMBER_WEIGHT;
    if (role === 'scaler') bonus += Math.min(counter, clusterCap) * SCALER_WEIGHT;
  }
  return bonus;
}
